import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import { DailySummary } from "@/components/DailySummary";
import { TrendsChart } from "@/components/TrendsChart";
import { MealFeed } from "@/components/MealFeed";
import { MealDialog } from "@/components/MealDialog";
import { MealPlanCard } from "@/components/MealPlanCard";
import { ProfileDialog } from "@/components/ProfileDialog";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [meals, setMeals] = useState([]);
  const [days, setDays] = useState(7);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [s, t, m] = await Promise.all([
      api.get("/stats/daily", { params: { date: todayISO() } }),
      api.get("/stats/trends", { params: { days } }),
      api.get("/meals", { params: { days } }),
    ]);
    setStats(s.data);
    setTrends(t.data);
    setMeals(m.data);
  }, [days]);

  useEffect(() => {
    load().catch(() => toast.error("Could not load your meal data"));
  }, [load]);

  const save = async (payload) => {
    try {
      if (editing) {
        await api.put(`/meals/${editing.meal_id}`, payload);
        toast.success("Meal updated");
      } else {
        await api.post("/meals", payload);
        toast.success("Meal added");
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
    } catch {
      toast.error("Could not save meal");
    }
  };

  const remove = async (meal) => {
    try {
      await api.delete(`/meals/${meal.meal_id}`);
      toast.success("Meal deleted");
      await load();
    } catch {
      toast.error("Could not delete meal");
    }
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="dashboard">
      <AppHeader
        onAdd={() => {
          setEditing(null);
          setDialogOpen(true);
        }}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-moss">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
          })}
        </p>
        <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-deep-olive">
          Hello, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="mt-3 max-w-xl text-base text-stone-grey leading-relaxed">
          Everything you log on mobile lands here. Same database, bigger picture.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-8">
            <DailySummary stats={stats} />
            <MealPlanCard stats={stats} onEditProfile={() => setProfileOpen(true)} />
            <TrendsChart trends={trends} days={days} onDaysChange={setDays} />
          </div>
          <div className="lg:col-span-4">
            <MealFeed
              meals={meals}
              onEdit={(m) => {
                setEditing(m);
                setDialogOpen(true);
              }}
              onDelete={remove}
            />
          </div>
        </div>
      </main>

      <MealDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditing(null);
        }}
        meal={editing}
        onSubmit={save}
      />

      <ProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        profile={stats?.profile}
        onSaved={() => load()}
      />
    </div>
  );
}
